import { Cluster, ClusterSettings, Gpu, Job, SupportedGpuMake } from '@typings';
import { SUPPORTED_GPU_MAKES } from '@constants';
import { as } from '@utils/as';
import { ensureArray } from '@utils/ensureArray';
import { isGpuSpec } from '@utils/guards';

type GetGpuOptions = {
  withPlaceholder?: boolean;
};

export const getGpuMakeKeys = (
  gpuMake: SupportedGpuMake,
): {
  gpuModelKey: keyof Gpu.Record;
  gpuModelNameKey: keyof Gpu.RecordNameOld;
} => ({
  gpuModelKey: `${gpuMake}Gpu`,
  gpuModelNameKey: `${gpuMake}GpuModel`,
});

export const getGpu = (
  resource:
    | Cluster.ResourcePreset
    | ClusterSettings.NodePool
    | Cluster.ResourcePoolType
    | Job.ContainerResources,
  options?: GetGpuOptions,
) => {
  const { withPlaceholder } = as.o<GetGpuOptions>(options);
  const placeholder = withPlaceholder ? [as.gpu(null)] : [];

  const gpus = SUPPORTED_GPU_MAKES.reduce<Gpu.Profile[]>((models, gpuMake) => {
    const gpuModel: keyof Gpu.Record = `${gpuMake}Gpu`;
    const isNewRecord = isGpuSpec(resource[gpuModel]);
    const gpuName: keyof Gpu.RecordNameOld = `${gpuMake}GpuModel`;

    const gpu = isNewRecord
      ? {
          gpuQuantity: (resource as Gpu.Record)[gpuModel]?.count,
          gpuModelName: (resource as Gpu.Record)[gpuModel]?.model,
          gpuMemory: (resource as Gpu.Record)[gpuModel]?.memory,
        }
      : {
          gpuQuantity: (resource as Gpu.RecordOld)[gpuModel],
          gpuModelName: (resource as Gpu.RecordOld)[gpuName],
        };

    if (gpu.gpuQuantity) {
      return [...models, { ...(gpu as Gpu.Profile), gpuMake }];
    }

    return models;
  }, []);

  return gpus.length ? gpus : placeholder;
};

export const getGpuFromNodePool = (
  nodePool: ClusterSettings.NodePool | Cluster.ResourcePoolType,
  options?: GetGpuOptions,
) => getGpu(nodePool, options);
export const getGpuFromResourcePreset = (
  resourcePreset: Cluster.ResourcePreset,
  options?: GetGpuOptions,
) => getGpu(resourcePreset, options);

export const parseGpuModels = (
  gpus: Gpu.Profile | Gpu.Profile[] = [],
): Gpu.Model =>
  ensureArray(gpus).reduce<Gpu.Model>((models, { gpuMake, gpuModelName }) => {
    const { gpuModelNameKey } = getGpuMakeKeys(gpuMake);

    return gpuMake ? { ...models, [gpuModelNameKey]: gpuModelName } : models;
  }, {});

/**
 * Normalizes the resource preset by ensuring GPU specifications are correctly formatted
 *
 * { nvidiaGpu: 2, nvidiaGpuModel: 'RTX 4080' } > { nvidiaGpu: { count: 2, model: 'RTX 480' } }
 */
export const normalizeResourceGpuSpec = <
  Preset extends
    | Cluster.ResourcePreset
    | ClusterSettings.NodePool
    | Cluster.ResourcePoolType
    | Job.ContainerResources
    | Gpu.Model,
>(
  resource: Preset,
): Preset & Gpu.Record => {
  const resourceCopy = structuredClone(resource);

  SUPPORTED_GPU_MAKES.forEach((gpuMake) => {
    const gpuModel: keyof Gpu.Record = `${gpuMake}Gpu`;
    const isNewRecord = isGpuSpec(resourceCopy[gpuModel]);

    if (isNewRecord || !resource[gpuModel]) {
      return;
    }

    const gpuModelName: keyof Gpu.RecordNameOld = `${gpuMake}GpuModel`;

    resourceCopy[gpuModel] = {
      count: resourceCopy[gpuModel] as number,
      model: (resourceCopy as Gpu.RecordOld)[gpuModelName],
    } as Gpu.Spec;
  }, []);

  return resourceCopy as Preset & Gpu.Record;
};
